import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Lock, Shield, CheckCircle, Eye, EyeOff } from "lucide-react";
import Header from "@/components/Header";
import BottomBar from "@/components/BottomBar";

export default function CardPinSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const cardData = location.state?.cardData;

  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const handlePinChange = (value, field) => {
    value = value.replace(/\D/g, "");
    if (value.length > 4) return;

    if (field === "pin") {
      setPin(value);
    } else {
      setConfirmPin(value);
    }

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!pin || pin.length !== 4) {
      newErrors.pin = "PIN must be 4 digits";
    }

    if (!confirmPin || confirmPin.length !== 4) {
      newErrors.confirmPin = "Please confirm your PIN";
    } else if (pin !== confirmPin) {
      newErrors.confirmPin = "PINs do not match";
    }

    // Check for common weak PINs
    const weakPins = [
      "0000",
      "1111",
      "1234",
      "4321",
      "2222",
      "3333",
      "4444",
      "5555",
      "6666",
      "7777",
      "8888",
      "9999",
    ];
    if (weakPins.includes(pin)) {
      newErrors.pin = "Please choose a stronger PIN";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      setIsProcessing(true);

      // Simulate activation process
      setTimeout(() => {
        setIsProcessing(false);
        setIsCompleted(true);
      }, 2000);
    }
  };

  // Success Screen
  if (isCompleted) {
    return (
      <div className="min-h-screen bg-background">
        <Header showBack={false} />

        <div className="max-w-md mx-auto px-3 pb-20">
          <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] space-y-6">
            {/* Success Icon */}
            <div className="p-4 bg-green-100 dark:bg-green-900/20 rounded-full">
              <CheckCircle className="w-16 h-16 text-green-600 dark:text-green-400" />
            </div>

            {/* Success Message */}
            <div className="text-center space-y-3">
              <h2 className="text-2xl font-bold">Card Activated!</h2>
              <p className="text-muted-foreground">
                Your card ending in {cardData?.lastFour || "****"} has been
                successfully activated
              </p>
            </div>

            {/* Card Status */}
            <Card className="bg-card/80 w-full">
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      Status
                    </span>
                    <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                      Active
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      Card Number
                    </span>
                    <span className="text-sm font-semibold">
                      ****-****-****-{cardData?.lastFour || "****"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">PIN</span>
                    <span className="text-sm font-semibold">Set</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Info Message */}
            <div className="p-4 bg-blue-200/20  rounded-lg w-full">
              <p className="text-sm text-blue-700 dark:text-blue-300 text-center">
                You can now use your card for purchases and ATM withdrawals
              </p>
            </div>

            {/* Action Button */}
            <Button onClick={() => navigate("/mycard")} className="w-full h-12">
              Go to My Cards
            </Button>
          </div>
        </div>

        <BottomBar />
      </div>
    );
  }

  // PIN Setup Form
  return (
    <div className="min-h-screen bg-background">
      <Header
        showBack={true}
        onBackClick={() => navigate("/card-activation")}
      />

      <div className="max-w-md mx-auto px-3 pb-20">
        {/* Title */}
        <div className="mt-6 mb-6">
          <h1 className="text-2xl font-bold">Set Your PIN</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Create a 4-digit PIN for your card
          </p>
        </div>

        {/* Security Icon */}
        <div className="flex justify-center mb-6">
          <div className="p-6 bg-primary/10 rounded-full">
            <Shield className="w-12 h-12 text-primary" />
          </div>
        </div>

        {/* Form */}
        <Card className="bg-card/80">
          <CardContent className="p-6 space-y-4">
            {/* Create PIN */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Lock className="w-4 h-4 text-muted-foreground" />
                Create PIN
              </label>
              <div className="relative">
                <Input
                  type={showPin ? "text" : "password"}
                  placeholder="• • • •"
                  value={pin}
                  onChange={(e) => handlePinChange(e.target.value, "pin")}
                  className={`h-12 text-center text-2xl tracking-widest pr-12 ${
                    errors.pin ? "border-red-500" : ""
                  }`}
                  maxLength="4"
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPin ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.pin && (
                <p className="text-xs text-red-500">{errors.pin}</p>
              )}
            </div>

            {/* Confirm PIN */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Lock className="w-4 h-4 text-muted-foreground" />
                Confirm PIN
              </label>
              <div className="relative">
                <Input
                  type={showConfirmPin ? "text" : "password"}
                  placeholder="• • • •"
                  value={confirmPin}
                  onChange={(e) =>
                    handlePinChange(e.target.value, "confirmPin")
                  }
                  className={`h-12 text-center text-2xl tracking-widest pr-12 ${
                    errors.confirmPin ? "border-red-500" : ""
                  }`}
                  maxLength="4"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPin(!showConfirmPin)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPin ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.confirmPin && (
                <p className="text-xs text-red-500">{errors.confirmPin}</p>
              )}
            </div>

            {/* PIN Requirements */}
            <div className="p-3 bg-muted/50 rounded-lg space-y-2">
              <p className="text-xs font-medium">PIN Requirements:</p>
              <ul className="space-y-1 text-xs text-muted-foreground">
                <li className="flex items-start gap-1">
                  <span>•</span>
                  <span>Must be exactly 4 digits</span>
                </li>
                <li className="flex items-start gap-1">
                  <span>•</span>
                  <span>Cannot be simple sequences (1234, 0000)</span>
                </li>
                <li className="flex items-start gap-1">
                  <span>•</span>
                  <span>Keep your PIN confidential</span>
                </li>
              </ul>
            </div>

            {/* Security Notice */}
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <p className="text-xs text-yellow-700 dark:text-yellow-300">
                Never share your PIN with anyone, including bank staff
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="mt-6">
          <Button
            onClick={handleSubmit}
            disabled={isProcessing}
            className="w-full h-12"
          >
            {isProcessing ? "Activating Card..." : "Activate Card"}
          </Button>
        </div>
      </div>

      <BottomBar />
    </div>
  );
}
